import React, { useContext, useEffect, useMemo, useState } from 'react'
import { AppContext } from '../../context'
import { ActionType } from '../../context/AppContextReducer'
import { Office, OfficeType, useFetchAllOfficesLazyQuery } from '../../../generated'
import { useDebounce } from 'use-debounce'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST } from '../../constants'
import { Alert } from '../common/Alert'
import { CardBox } from '../../theme/styleComponents'
import { ContentHead } from '../common/ContentHead'
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { anchorOrigin, transformOrigin, flexCenter } from '../../theme/styleConstants'
import { EmptyData } from '../common/EmptyData'
import OfficeModal from './OfficeModal'
import OfficeListing from './OfficeListing'
import { SearchWithKeypress } from '../common/SearchWithKeypress'
import { useSearchParams } from 'react-router-dom'

enum FilterOfficeBy {
  PROFIT_CENTER = 'profitCenter',
  LOCATION = 'location'
}

const OfficeContainer = () => {
  const { searchValue, dispatch: reducerDispatch, isOpen } = useContext(AppContext)
  const handleClose = () => reducerDispatch({ type: ActionType.SET_IS_OPEN, isOpen: false })
  const [searchParams, setSearchParams] = useSearchParams()
  const [offices, setOffices] = useState<Office[]>([])
  const [debouncedSearchValue] = useDebounce(searchValue, 500)
  const officeType = (searchParams.get('type') || FilterOfficeBy.PROFIT_CENTER) as FilterOfficeBy

  const [fetchAllVendors, { loading, refetch }] = useFetchAllOfficesLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { fetchAllOffices } = data || {}
      const { offices, response } = fetchAllOffices || {}
      const { status } = response || {}
      if (status === 200) {
        setOffices(offices as Office[])
      }
    }
  })
  useEffect(() => {
    return () => {
      reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
      reducerDispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    fetchAllVendors({
      variables: {
        fetchOfficesInput: {
          keyword: debouncedSearchValue
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue])

  const filteredOffices = useMemo(() => {
    if (officeType === FilterOfficeBy.PROFIT_CENTER) {
      return offices?.filter(office => office?.type === OfficeType.ProfitCenter)
    }

    return offices.filter(office => office?.type === OfficeType.Location)
  }, [offices, officeType])

  const handleOfficeTypeChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    searchParams.set('type', value)
    setSearchParams(searchParams)
  }

  const isEmpty = !loading && !filteredOffices.length

  return (
    <>
      <CardBox marginTop={3}>
        <ContentHead title={'Profit Centers'} buttonText={'Add Profit Center'} />
        <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} m={2}>
          <SearchWithKeypress searchPlaceholder={'Search'} />

          <FormControl>
            <InputLabel id="filterBy">Filter By:</InputLabel>
            <Select
              labelId="filterBy"
              id="filterBy"
              label={'Filter By:'}
              onChange={handleOfficeTypeChange}
              displayEmpty={true}
              value={officeType || FilterOfficeBy.PROFIT_CENTER}
              sx={{ width: 200 }}
              MenuProps={{
                anchorOrigin,
                transformOrigin,
                style: {
                  maxHeight: 400
                }
              }}
            >
              <MenuItem value={FilterOfficeBy.PROFIT_CENTER}>Profit Center</MenuItem>
              <MenuItem value={FilterOfficeBy.LOCATION}>Location</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {isEmpty ? (
          <Box sx={{ ...flexCenter, height: 'calc(100vh - 265px)' }}>
            <EmptyData title={'No Profit Center found'} />
          </Box>
        ) : (
          <OfficeListing offices={filteredOffices} loading={loading} refetch={refetch} />
        )}
      </CardBox>

      <OfficeModal handleClose={handleClose} isOpen={isOpen} refetch={refetch} />
    </>
  )
}

export default OfficeContainer
